import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Nomenclature, NomenclatureComposant, NomenclaturesService } from '../../../services/nomenclatures.service';
import { Composant, ComposantsService } from '../../../services/composants.service';

@Component({
  selector: 'app-modifier-nomenclature',
  imports: [CommonModule, FormsModule],
  templateUrl: './modifier-nomenclature.component.html',
  styleUrl: './modifier-nomenclature.component.scss'
})
export class ModifierNomenclatureComponent implements OnInit {
  private pendingNomenclatureId: number | null = null;

  protected nomenclatures: Nomenclature[] = [];
  protected composants: Composant[] = [];
  protected nomenclatureComposants: NomenclatureComposant[] = [];
  protected selectedNomenclatureId = '';
  protected selectedComposantId = '';
  protected selectedQuantite = '';

  protected numeroNomenclature = '';
  protected nomNomenclature = '';
  protected dateCreation = '';

  protected loading = false;
  protected loadingNomenclatures = false;
  protected loadingComposants = false;
  protected addingComposant = false;
  protected removingComposantId: number | null = null;
  protected editingComposantId: number | null = null;
  protected editingQuantite = '';
  protected updatingComposantId: number | null = null;
  protected success = '';
  protected error = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly nomenclaturesService: NomenclaturesService,
    private readonly composantsService: ComposantsService,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const queryNomenclatureId = Number(params['nomenclatureId']);
      if (Number.isFinite(queryNomenclatureId) && queryNomenclatureId > 0) {
        this.pendingNomenclatureId = queryNomenclatureId;
        this.selectedNomenclatureId = String(queryNomenclatureId);
        if (this.nomenclatures.length > 0) {
          this.onNomenclatureSelect();
          this.pendingNomenclatureId = null;
        }
      }
    });

    this.loadNomenclatures();
    this.loadComposants();
  }

  protected onNomenclatureSelect(): void {
    const selected = this.nomenclatures.find((n) => n.id === Number(this.selectedNomenclatureId));
    if (selected) {
      this.numeroNomenclature = String(selected.numero_nomenclature);
      this.nomNomenclature = selected.nom ?? '';
      this.dateCreation = selected.date_creation;
      this.loadNomenclatureComposants();
    } else {
      this.nomenclatureComposants = [];
    }
  }

  protected updateNomenclatureInfo(): void {
    this.error = '';
    this.success = '';

    if (!this.selectedNomenclatureId) {
      this.error = 'Veuillez sélectionner une nomenclature.';
      return;
    }

    const numero = Number(this.numeroNomenclature);
    if (!Number.isInteger(numero) || numero < 0) {
      this.error = 'Le numéro de nomenclature doit être un entier positif.';
      return;
    }

    if (String(numero).length > 9) {
      this.error = 'Le numéro de nomenclature ne doit pas dépasser 9 caractères.';
      return;
    }

    const nom = this.nomNomenclature.trim();
    if (!nom) {
      this.error = 'Le nom de la nomenclature est requis.';
      return;
    }

    this.loading = true;
    this.zone.run(() => {
      this.nomenclaturesService
        .update(Number(this.selectedNomenclatureId), {
          numero_nomenclature: numero,
          nom,
          date_creation: this.dateCreation || undefined,
        })
        .subscribe({
          next: (updated) => {
            this.success = 'Nomenclature mise à jour avec succès.';
            this.numeroNomenclature = String(updated.numero_nomenclature);
            this.nomNomenclature = updated.nom ?? '';
            this.dateCreation = updated.date_creation;

            const idx = this.nomenclatures.findIndex((n) => n.id === updated.id);
            if (idx >= 0) {
              this.nomenclatures[idx] = updated;
            }

            this.loading = false;
            this.cdr.markForCheck();
          },
          error: (err: HttpErrorResponse) => {
            this.error = `Erreur lors de la mise à jour: ${this.getErrorMessage(err)}`;
            this.loading = false;
            this.cdr.markForCheck();
          }
        });
    });
  }

  protected addComposantToNomenclature(): void {
    this.error = '';
    this.success = '';

    if (!this.selectedNomenclatureId) {
      this.error = 'Veuillez sélectionner une nomenclature.';
      return;
    }

    if (!this.selectedComposantId) {
      this.error = 'Veuillez sélectionner un composant.';
      return;
    }

    const quantite = parseInt(this.selectedQuantite, 10);
    if (isNaN(quantite) || quantite < 1) {
      this.error = 'La quantité doit être un nombre positif.';
      return;
    }

    this.addingComposant = true;
    this.zone.run(() => {
      this.nomenclaturesService.addComposant(Number(this.selectedNomenclatureId), Number(this.selectedComposantId), quantite).subscribe({
        next: () => {
          this.success = 'Composant ajouté avec succès.';
          this.selectedComposantId = '';
          this.selectedQuantite = '';
          this.loadNomenclatureComposants();
          this.addingComposant = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de l'ajout: ${this.getErrorMessage(err)}`;
          this.addingComposant = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected removeComposantFromNomenclature(composantId: number): void {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce composant de la nomenclature?')) {
      return;
    }

    this.removingComposantId = composantId;
    this.zone.run(() => {
      this.nomenclaturesService.removeComposant(Number(this.selectedNomenclatureId), composantId).subscribe({
        next: () => {
          this.success = 'Composant supprimé avec succès.';
          this.loadNomenclatureComposants();
          this.removingComposantId = null;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
          this.removingComposantId = null;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected getComposantName(composantId: number): string {
    const composant = this.composants.find((c) => c.id === composantId);
    return composant ? composant.nom : `Composant ${composantId}`;
  }

  protected getPrixUnitaire(composantId: number): number | null {
    const composant = this.composants.find((c) => c.id === composantId);
    if (!composant || composant.prix === null) {
      return null;
    }

    const emballage = parseFloat(composant.standard_emballage ?? '1');
    if (isNaN(emballage) || emballage <= 0) {
      return null;
    }

    return composant.prix / emballage;
  }

  protected getPrixTotal(composantId: number, quantite: number): number | null {
    const prixUnitaire = this.getPrixUnitaire(composantId);
    if (prixUnitaire === null) {
      return null;
    }
    return prixUnitaire * quantite;
  }

  protected startEditingComposant(nc: NomenclatureComposant): void {
    this.editingComposantId = nc.composant_id;
    this.editingQuantite = String(nc.quantite);
  }

  protected cancelEditingComposant(): void {
    this.editingComposantId = null;
    this.editingQuantite = '';
  }

  protected updateComposantQuantite(composantId: number): void {
    this.error = '';
    this.success = '';

    const quantite = parseInt(this.editingQuantite, 10);
    if (isNaN(quantite) || quantite < 1) {
      this.error = 'La quantité doit être un nombre positif.';
      return;
    }

    this.updatingComposantId = composantId;
    this.zone.run(() => {
      this.nomenclaturesService.updateComposant(Number(this.selectedNomenclatureId), composantId, quantite).subscribe({
        next: () => {
          this.success = 'Quantité mise à jour avec succès.';
          this.editingComposantId = null;
          this.editingQuantite = '';
          this.loadNomenclatureComposants();
          this.updatingComposantId = null;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la mise à jour: ${this.getErrorMessage(err)}`;
          this.updatingComposantId = null;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadNomenclatures(): void {
    this.loadingNomenclatures = true;
    this.zone.run(() => {
      this.nomenclaturesService.list().subscribe({
        next: (nomenclatures) => {
          this.nomenclatures = nomenclatures;
          if (this.pendingNomenclatureId) {
            const exists = this.nomenclatures.some((n) => n.id === this.pendingNomenclatureId);
            if (exists) {
              this.selectedNomenclatureId = String(this.pendingNomenclatureId);
              this.onNomenclatureSelect();
            }
            this.pendingNomenclatureId = null;
          }
          this.loadingNomenclatures = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement des nomenclatures: ${this.getErrorMessage(err)}`;
          this.loadingNomenclatures = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadComposants(): void {
    this.loadingComposants = true;
    this.zone.run(() => {
      this.composantsService.list().subscribe({
        next: (composants) => {
          this.composants = composants;
          this.loadingComposants = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          console.error('Erreur lors du chargement des composants:', err);
          this.loadingComposants = false;
        }
      });
    });
  }

  private loadNomenclatureComposants(): void {
    this.zone.run(() => {
      this.nomenclaturesService.getComposants(Number(this.selectedNomenclatureId)).subscribe({
        next: (composants) => {
          this.nomenclatureComposants = Array.isArray(composants) ? composants : [];
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          console.error('Erreur lors du chargement des composants de la nomenclature:', err);
          this.nomenclatureComposants = [];
        }
      });
    });
  }

  protected editComposant(composantId: number): void {
    this.router.navigate(['/intranet/gestion-stocks/composants/modifier'], {
      queryParams: { id: composantId }
    });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    if (err.error?.detail) {
      return err.error.detail;
    }

    if (err.error?.message) {
      return err.error.message;
    }

    return err.statusText || 'Erreur inconnue';
  }
}
